import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, ProductContextModel, withProduct } from "@insite/client-framework/Components/ProductContext";
import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import * as React from "react";
import { css } from "styled-components";

interface OwnProps {
    product: CartLineModel | ProductContextModel | OrderLineModel;
    extendedStyles?: ProductDescriptionStyles;
}

type Props = WidgetProps & HasProduct & OwnProps;

export interface ProductDetailsPageTitleStyles {
    container?: InjectableCss;
    brandGridItem?: InjectableCss;
    brandStyles?: ProductBrandStyles;
    titleGridItem?: InjectableCss;
    titleText?: TypographyProps;
    descriptionStyles?: ProductDescriptionStyles;
}

export const pageTitleStyles: ProductDetailsPageTitleStyles = {
    container: {
        css: css`
            display: flex;
            align-items: flex-start;
            gap: 5px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        gap: 5px;
                    `,
                    css`
                        gap: 5px;
                    `,
                    css`
                        gap: 5px;
                    `,
                    null,
                    null,
                ])};
        `,
    },
    brandGridItem: {
        css: css`
            span {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.largest};
                line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
                color: ${getColor("text.main")} !important;
                white-space: nowrap;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            line-height: 32px;
                        `,
                        css`
                            line-height: 32px;
                        `,
                        css`
                            line-height: 32px;
                        `,
                        null,
                        null,
                    ])}
            }
        `,
    },
    brandStyles: {
        logoImage: {
            css: css`
                img {
                    max-width: 150px;
                    max-height: 150px;
                }
            `,
        },
        nameText: {
            size: 16,
            weight: "normal",
        },
    },
    titleGridItem: {
        css: css``,
    },
    titleText: {
        variant: "h2",
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: initial;
            font-size: ${TYPOGRAPHY.size.largest};
            line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
            color: ${getColor("text.main")};
            margin-bottom: 0px;
        `,
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                pointer-events: none;
                cursor: auto;
                span {
                    font-family: ${TYPOGRAPHY.fontFamily.title};
                    font-style: normal;
                    font-weight: normal;
                    font-size: ${TYPOGRAPHY.size.largest};
                    line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
                    color: ${getColor("text.main")} !important;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                font-weight: 600;
                                line-height: 32px;
                            `,
                            css`
                                font-weight: 600;
                                line-height: 32px;
                            `,
                            css`
                                font-weight: 600;
                                line-height: 32px;
                            `,
                            null,
                            null,
                        ])}
                }
            `,
        },
    },
};

const styles = pageTitleStyles;

const ProductDetailsPageTitle: React.FC<Props> = ({ product, productInfo }) => {
    const productContext = {
        product,
        productInfo,
        // onUnitOfMeasureChanged: updateUnitOfMeasure,
        // onQtyOrderedChanged: updateQuantity,
    };
    return (
        <StyledWrapper {...styles.container}>
            {product.brand && (
                <StyledWrapper {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} showLogo={false} extendedStyles={styles.brandStyles} />
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.titleGridItem}>
                <ProductDescription product={productContext} extendedStyles={styles.descriptionStyles} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsPageTitle),
    definition: {
        displayName: "Page Title",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
